import { makeAutoObservable } from "mobx";

import { ApiError } from "@api";
import { CliMatch } from "@types";

import {
  Highlight,
  RuleValidationError,
  RunResponse,
} from "@pages/editor/types";
import { makeMatchHighlight } from "@pages/editor/utils";

import { Bundle } from "./Bundle";

interface CommonResult {
  bundle: Bundle;
  errorsCount: number;
  matchesCount: number;
  testFailuresCount: number;
  // Target text for which is this result valid for
  targetText: string;
}

export class SuccessResult implements CommonResult {
  bundle: Bundle;
  response: RunResponse;
  findings: CliMatch[];
  numTestFailures: number;
  overridenHighlights: Highlight[] | null;
  errorsCount = 0;
  targetText: string;

  constructor(bundle: Bundle, response: RunResponse) {
    makeAutoObservable(this);
    this.bundle = bundle;
    this.targetText = bundle.targetText;
    this.response = response;
    this.findings = response.semgrep_result.output.results;
    this.numTestFailures = response.test_result.reduce(
      (memo, tr) => memo + (tr.status === "FAILURE" ? 1 : 0),
      0
    );
    this.errorsCount = this.errors.length;
    this.overridenHighlights = null;
  }

  get highlights(): Highlight[] {
    if (this.isDirty) {
      return [];
    }
    return (
      this.overridenHighlights ||
      this.findings.map((result) => makeMatchHighlight(result))
    );
  }

  get isDirty(): boolean {
    return this.bundle.targetText !== this.targetText;
  }

  get matchesCount(): number {
    return this.findings.length;
  }

  get testFailuresCount(): number {
    return this.numTestFailures ?? 0;
  }

  get runtimeSeconds(): number {
    return this.response.semgrep_result.run_time;
  }

  get errors(): RuleValidationError[] {
    return this.response.semgrep_result.output.errors.map((err) => ({
      message: err.message ?? "unknown",
      severity: "Error",
      source: "Engine",
      code: err.type,
    }));
  }
}

export class ErrorResult implements CommonResult {
  bundle: Bundle;
  errors: RuleValidationError[];
  targetText: string;

  // Errors and matches aren't usually exclusive, but for this class
  // they are in the vast majority of cases. Subclass if otherwise.
  matchesCount = 0;
  testFailuresCount = 0;

  constructor(bundle: Bundle, errors: RuleValidationError[]) {
    makeAutoObservable(this);
    this.bundle = bundle;
    this.errors = errors;
    this.targetText = bundle.targetText;
  }

  get highlights(): Highlight[] {
    return [];
  }

  get isDirty(): boolean {
    return this.bundle.targetText !== this.targetText;
  }

  get errorsCount(): number {
    return this.errors.length;
  }

  static fromApiResult(bundle: Bundle, error: ApiError) {
    let error_: RuleValidationError =
      error.statusCode === 400
        ? typeof error.body?.error === "string"
          ? { message: error.body?.error, severity: "Error" }
          : { message: "API ERROR - INVALID REQUEST", severity: "Error" }
        : { message: "UNKNOWN API ERROR", severity: "Error" };
    return new ErrorResult(bundle, [error_]);
  }
}

export type Result = SuccessResult | ErrorResult;
